// @flow
import sha1 from 'js-sha1';
import type { MessageDescriptor } from 'react-intl';
import { filter as filterAsync, map as mapAsync } from 'p-iteration';
import each from 'lodash/forEach';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isNaN from 'lodash/isNaN';
import set from 'lodash/set';
import size from 'lodash/size';
import includes from 'lodash/includes';
import type {
  UploadFileWithAPIOptions,
  UploadItem,
} from 'box-ui-elements/es/common/types/upload';
import { getFile } from 'box-ui-elements/es/utils/uploads';
import {
  TYPE_GROUP,
  TYPE_TEXT_FIELD,
  TYPE_SELECT_FIELD,
  TYPE_UPLOAD_FIELD,
  TYPE_DATE_FIELD,
  TEXTTYPE_EMAIL,
  TEXTTYPE_NUMBER,
  REGEX_EMAIL_VALIDATION,
  REGEX_UNICODE_BMP_VALIDATION,
  TYPE_CHECKBOX_FIELD,
  TYPE_RADIO_FIELD,
} from '../../constants';
import { ERRORS } from './FormErrors';

type Error = MessageDescriptor & {
  values?: { info?: string },
};

type ErrorWithOverride = Error & {
  notificationOverride?: Error,
};

const isEmailValid = (email: string) =>
  REGEX_EMAIL_VALIDATION.test(String(email).toLowerCase());

const isNumeric = (n: string) => !isNaN(Number(n) - parseFloat(n));

const hasSpecialChars = (text: string) =>
  REGEX_UNICODE_BMP_VALIDATION.test(String(text));

export const validateTextField = (
  textType: string,
  required: boolean,
  value: string,
): ErrorWithOverride | typeof undefined => {
  if (required && isEmpty(value)) {
    return ERRORS.REQUIRED_FIELD;
  }
  if (!isEmpty(value)) {
    switch (textType) {
      case TEXTTYPE_EMAIL:
        if (!isEmailValid(value)) {
          return ERRORS.INVALID_EMAIL_ADDRESS;
        }
        break;
      case TEXTTYPE_NUMBER:
        if (!isNumeric(value)) {
          return ERRORS.NOT_A_NUMBER;
        }
        break;
      default:
        if (hasSpecialChars(value)) {
          const obj = {
            ...ERRORS.SPECIAL_CHARS,
            notificationOverride: ERRORS.SPECIAL_CHARS_NOTIFICATION,
          };
          return obj;
        }
        break;
    }
  }
  return undefined;
};

export const validateSelectField = (
  required: boolean,
  values: Array<string>,
): ErrorWithOverride | typeof undefined => {
  if (required && size(values) === 0) {
    return ERRORS.REQUIRED_FIELD;
  }
  return undefined;
};

export const validateListField = (
  required: boolean,
  values: Array<string>,
): ErrorWithOverride | typeof undefined => {
  if (required && size(values) === 0) {
    return ERRORS.REQUIRED_LIST_FIELD;
  }
  return undefined;
};

export const validateDateField = (
  required: boolean,
  value: string,
): ErrorWithOverride | typeof undefined => {
  if (required && isEmpty(value)) {
    return ERRORS.REQUIRED_FIELD;
  }
  return undefined;
};

export const validateUploadField = (
  required: boolean,
  values: Array<any>,
): ErrorWithOverride | typeof undefined => {
  if (required && size(values) === 0) {
    return ERRORS.REQUIRED_UPLOAD_FIELD;
  }
  return undefined;
};

export const filterFilesNotAlreadyAdded = async (
  items: Array<UploadFileWithAPIOptions | File>,
  itemsAlreadyInQueue: Array<UploadItem>,
) => {
  const newItems = [];
  const sameName = [];

  // filter on size/name equality first as a quick pass

  const nameSizeAlreadyInQueue = itemsAlreadyInQueue.map(
    ({ name, size: fileSize }) => `${name}|${fileSize}`,
  );

  items.forEach(item => {
    const { name, size: fileSize } = getFile(item);
    if (includes(nameSizeAlreadyInQueue, `${name}|${fileSize}`)) {
      sameName.push({ item, matcher: `${name}|${fileSize}` });
    } else {
      newItems.push(item);
    }
  });

  if (sameName.length === 0) return newItems;

  // hash the matching files, and compare shas to determine equality

  const readFile = (blob: Blob) => {
    const reader = new window.FileReader();
    return new Promise((resolve, reject) => {
      reader.readAsArrayBuffer(blob);
      reader.onload = () => {
        resolve({
          buffer: reader.result,
          readCompleteTimestamp: Date.now(),
        });
      };
      reader.onerror = reject;
    });
  };
  const getSHA = async (file: File) => {
    const { buffer } = await readFile(file);
    return sha1(buffer);
  };

  const sameItemsAlreadyInQueue = itemsAlreadyInQueue.filter(
    ({ name, size: fileSize }) =>
      includes(
        sameName.map(item => item.matcher),
        `${name}|${fileSize}`,
      ),
  );

  const sameShasAlreadyInQueue = await mapAsync(
    sameItemsAlreadyInQueue,
    async item => {
      const { file, name, fileSize } = item;
      try {
        return getSHA(file);
      } catch (e) {
        // arbitrary just to be used for matching
        return `${name}|${fileSize}`;
      }
    },
  );

  const newSameItems = await filterAsync(sameName, async ({ item }) => {
    const uploadFile = getFile(item);
    const { name, size: fileSize } = uploadFile;
    let sha;
    try {
      sha = await getSHA(uploadFile);
    } catch (e) {
      // arbitrary just to be used for matching
      sha = `${name}|${fileSize}`;
    }
    return !includes(sameShasAlreadyInQueue, sha);
  });
  return [...newItems, ...newSameItems.map(item => item.item)];
};

const setError = (
  id: string,
  errors: { [id: string]: Error },
  error: ErrorWithOverride,
): void => {
  const { notificationOverride, ...inlineError } = error;
  set(errors, id, inlineError);
  if (notificationOverride) {
    set(errors, 'notificationOverride', notificationOverride);
  }
};

type ReturnedErrors = {
  [id: string]: Error,
  notificationOverride?: Error,
};

export const validateFormResponse = (
  components: Object = {},
  itemQueues: Object = {},
  response: Object = {},
): {
  errorNotification: Object,
  errors: ReturnedErrors,
} => {
  const errors = {};
  each(components, component => {
    const { id, type, required, visible = true } = component;
    if (visible) {
      switch (type) {
        case TYPE_GROUP:
          break;
        case TYPE_UPLOAD_FIELD: {
          const error = validateUploadField(
            required,
            get(itemQueues, `${id}.items`),
          );
          if (error) {
            setError(id, errors, error);
          }
          break;
        }
        case TYPE_TEXT_FIELD: {
          const { textType } = component;
          const error = validateTextField(
            textType,
            required,
            get(response, id),
          );
          if (error) {
            setError(id, errors, error);
          }
          break;
        }
        case TYPE_SELECT_FIELD: {
          const error = validateSelectField(required, get(response, id));
          if (error) {
            setError(id, errors, error);
          }
          break;
        }
        case TYPE_DATE_FIELD: {
          const error = validateDateField(required, get(response, id));
          if (error) {
            setError(id, errors, error);
          }
          break;
        }
        case TYPE_CHECKBOX_FIELD:
        case TYPE_RADIO_FIELD: {
          const error = validateListField(required, get(response, id));
          if (error) {
            setError(id, errors, error);
          }
          break;
        }
        default:
      }
    }
  });

  return errors;
};
